import { forwardRef } from 'react';

import {
    AiPromptModal,
    type AiPromptIntegrationMode,
    type AiPromptModalConfig,
    type AiPromptModalRef,
} from '@/components/AiPromptModal/AiPromptModal';

export type RecommendationIntegrationMode = AiPromptIntegrationMode;
export type AiRecommendationPromptModalRef = AiPromptModalRef;

interface AiRecommendationPromptModalProps {
    /**
     * Runs the real generation for the two answers and resolves to the generated HTML (or null on
     * failure/timeout). The modal advances to the integration step only on a non-null result.
     */
    onGenerate: (feeling: string, priority: string) => Promise<string | null>;
    /** Integrates the generated HTML into the editor, either appended or replacing the current text. */
    onIntegrate: (mode: RecommendationIntegrationMode, generatedHtml: string) => void;
    /** When false, "Add to existing text" is disabled (the section has no current text to append to). */
    canAdd?: boolean;
    dataTestId?: string;
}

const RECOMMENDATIONS_CONFIG: AiPromptModalConfig = {
    headerTitleKey: 'postReviewPrepareMeeting.aiRecommendations.title',
    imageAltKey: 'postReviewPrepareMeeting.aiRecommendations.imageAlt',
    overlayGeneratingKey: 'postReviewPrepareMeeting.aiRecommendations.overlay.generating',
    overlayMessageKey: 'postReviewPrepareMeeting.aiRecommendations.overlay.message',
    generateButtonKey: 'postReviewPrepareMeeting.aiRecommendations.step2.generateButton',
    steps: [
        {
            iconName: 'essential-flash',
            questionKey: 'postReviewPrepareMeeting.aiRecommendations.step1.question',
            placeholderKey: 'postReviewPrepareMeeting.aiRecommendations.step1.placeholder',
            rows: 6,
        },
        {
            iconName: 'files-folder',
            questionKey: 'postReviewPrepareMeeting.aiRecommendations.step2.question',
            placeholderKey: 'postReviewPrepareMeeting.aiRecommendations.step2.placeholder',
            rows: 4,
        },
    ],
    integration: {
        titleKey: 'postReviewPrepareMeeting.aiRecommendations.integrate.title',
        add: {
            titleKey: 'postReviewPrepareMeeting.aiRecommendations.integrate.addTitle',
            descriptionKey: 'postReviewPrepareMeeting.aiRecommendations.integrate.addDescription',
        },
        replace: {
            titleKey: 'postReviewPrepareMeeting.aiRecommendations.integrate.replaceTitle',
            descriptionKey:
                'postReviewPrepareMeeting.aiRecommendations.integrate.replaceDescription',
        },
    },
};

export const AiRecommendationPromptModal = forwardRef<
    AiRecommendationPromptModalRef,
    AiRecommendationPromptModalProps
>(({ onGenerate, onIntegrate, canAdd, dataTestId = 'ai-recommendation-prompt-modal' }, ref) => (
    <AiPromptModal<string>
        ref={ref}
        config={RECOMMENDATIONS_CONFIG}
        onGenerate={(answers) => onGenerate(answers[0], answers[1])}
        onIntegrate={onIntegrate}
        canAdd={canAdd}
        dataTestId={dataTestId}
    />
));

AiRecommendationPromptModal.displayName = 'AiRecommendationPromptModal';
